import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActionArea from "@mui/material/CardActionArea";
import { useTheme } from "@mui/material/styles";
import type { BaseMoleculeProps } from "../../types";
import { Image, PriceDisplay } from "../../atoms";
import type { MenuProduct } from "@/types";
import { imagorService } from "@/helpers";

export interface ProductSmallCardProps extends BaseMoleculeProps {
  /**
   * Product item data
   */
  item: MenuProduct;

  /**
   * Click handler
   */
  onSelect: () => void;

  /**
   * Disabled state
   */
  disabled?: boolean;

  /**
   * Card width constraints
   */
  maxWidth?: number;
  minWidth?: number;

  /**
   * Show price
   */
  showPrice?: boolean;

  /**
   * Custom price formatter
   */
  formatPrice?: (price: number) => string;

  /**
   * Image optimization settings
   */
  imageSize?: {
    width: number;
    height: number;
  };
}

const ProductSmallCardComponent: React.FC<ProductSmallCardProps> = ({
  item,
  onSelect,
  disabled = false,
  maxWidth = 200,
  minWidth = 100,
  imageSize = { width: 110, height: 110 },
  variant = "default",
  className,
}) => {
  const theme = useTheme();
  const displayPrice = item.pricePoint || 0;
  const imageUrl = item.images?.[0]?.url;

  // Transform image URL with imagor service
  const transformedImageUrl = useMemo(() => {
    if (!imageUrl) return null;
    try {
      return imagorService.transformUrl(imageUrl, {
        width: imageSize.width,
        height: imageSize.height,
        smart: true,
      });
    } catch (error) {
      console.error("Error transforming image URL:", error);
      return imageUrl;
    }
  }, [imageUrl, imageSize.width, imageSize.height]);

  return (
    <Card
      className={className}
      elevation={0}
      sx={{
        backgroundColor: theme.palette.action.hover,
        borderRadius: 4,
        width: "100%",
        maxWidth,
        minWidth,
        flex: "1 1 0",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
        "&:hover": !disabled
          ? {
              backgroundColor: theme.palette.action.selected,
              transform: "translateY(-2px)",
              transition: theme.transitions.create("transform", {
                duration: theme.transitions.duration.short,
              }),
            }
          : {},
        // Variant-specific styling
        ...(variant === "compact" && {
          borderRadius: 2,
          maxWidth: 150,
        }),
        ...(variant === "expanded" && {
          borderRadius: 6,
          maxWidth: 250,
        }),
      }}
    >
      <CardActionArea
        onClick={onSelect}
        disabled={disabled}
        sx={{
          padding: 0.5,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        {/* Product Image */}
        <Image
          src={transformedImageUrl || ""}
          alt={item.name}
          responsive={true}
          showLoadingState={true}
          borderRadius={3}
          sx={{
            width: "100%",
            marginBottom: 1,
          }}
        />

        {/* Product Info */}
        <CardContent
          sx={{
            padding: 0,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          {/* Product Name */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              marginBottom: 0.5,
              flex: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: "12px",
                textAlign: "center",
                lineHeight: 1.1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                width: "100%",
              }}
            >
              {item.name}
            </Typography>
          </Box>

          {/* Price */}
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              fontWeight: 500,
              fontSize: 16,
            }}
          >
            <PriceDisplay value={displayPrice} />
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

// Apply memoization with performance monitoring
export const ProductSmallCard = ProductSmallCardComponent;

// Set display name for debugging
ProductSmallCard.displayName = "ProductSmallCard";
